import {
  formatValidQueryParamUrl,
  formatValidQueryParamUrlCommaSeparated
} from "utils/common";
import { createPaginatedEndpoint } from "utils/services.util";

import { emptySplitApi } from "./empty-split-api";
import type {
  BillingPaymentsFilters,
  BillingPaymentsResponse,
  BulkStaffTaskInput,
  BulkStaffTaskResult,
  BulkStartNewCadenceResponse,
  CadenceLevel,
  CollectEnrollmentOffer,
  CollectionCadenceTemplate,
  CollectionCounters,
  CollectionDispute,
  CollectionOutreach,
  CollectionPracticeSettings,
  CollectionRecord,
  CollectionsFilters,
  CollectionsResponse,
  CreateCadenceTemplateInput,
  CreateDisputeInput,
  OutreachScheduleFilters,
  OutreachScheduleResponse,
  SuggestedSalesResponse,
  SuggestedScope,
  TriggerOutreachInput,
  UpdateCadenceTemplateInput,
  UpdateCollectionInput,
  UpdateDisputeInput
} from "./types/collections.type";
import { CSVReportResponse } from "./types/reports.type";

export interface StartCollectionRequest {
  sale_id: number;
  cadence?: CadenceLevel;
}

export interface StartCollectionResponse extends CollectionRecord {}

export const DEFAULT_COLLECTIONS_LIMIT = 25;

export const COLLECTIONS_DEFAULT_QUERY =
  "{id,sale{id,service_date,total,status},patient_name, patient{id,first_name,last_name,full_name,email,phone},clinic{id,name,practice{id,name}},original_balance,current_balance,total_collected,status,cadence,invoice_date,days_in_collection,last_outreach_at,last_payment_date,last_payment_amount,outreach_count,days_overdue_at_enrollment,days_overdue_source,created_at}";

export const COLLECTION_DETAIL_QUERY =
  "{id,sale{id,service_date,total,status,items{id,description,total}},patient{id,first_name,last_name,full_name,email,phone},clinic{id,name,phone,practice{id,name,logo}},original_balance,current_balance,total_collected,status,cadence,cadence_started_at,invoice_date,days_in_collection,last_outreach_at,outreach_count,statuses{value,created_at},created_at,updated_at}";

export const OUTREACH_QUERY =
  "{id,record,scheduled_at,executed_at,notification_method,template_type,template_type_display,step_action,status,status_display,created_at}";

export const OUTREACH_SCHEDULE_QUERY =
  "{id,record{id,patient{id,full_name,phone,email},patient_name,outreach_count,current_balance,sale{id}},scheduled_at,executed_at,notification_method,notification_method_display,template_type,template_type_display,step_action,status,status_display,created_at}";

export const DISPUTE_QUERY =
  "{id,record{id,patient{id,first_name,last_name,full_name,email,phone},clinic{id,name},original_balance,current_balance},reason,resolution,status,created_at,updated_at}";

export const CADENCE_TEMPLATE_QUERY =
  "{id,practice,cadence_level,day_offset,notification_method,template_type,min_balance,step_action,assignee,staff_task_note,is_active,created_at,updated_at}";

export const COLLECTIONS_CSV_QUERY =
  "{id,patient_name,clinic_name,original_balance,current_balance,total_collected,invoice_date,days_in_collection,status_display,cadence_display,outreach_count,last_outreach_at,last_payment_date,last_payment_amount,created_at,sale_id}";

const COLLECTIONS_CSV_HEADER =
  "patient_name,clinic_name,original_balance,total_collected,current_balance,status_display,invoice_date,days_in_collection,outreach_count,last_outreach_at,last_payment_date,last_payment_amount,cadence_display,sale_id";

const COLLECTIONS_CSV_LABELS =
  "Patient,Clinic,Collection Balance,Total Collected,Remaining Balance,Status,Invoice Date,Days in Collection,Outreach Count,Last Outreach At,Last Payment Date,Last Payment Amount,Cadence,Invoice ID";

const COLLECTIONS_BILLING_CSV_HEADER =
  "payment_date,patient_name,sale_id,clinic_name,payment_amount,fee_total,collection_status";

const COLLECTIONS_BILLING_CSV_LABELS =
  "Payment Date,Patient,Invoice No.,Clinic,Payment Amount,Commission Fee,Collection Status";

const getDefaultCollectionsFilters = (filters: CollectionsFilters) => {
  return {
    offset: filters.offset ?? 0,
    limit: filters.limit ?? DEFAULT_COLLECTIONS_LIMIT,
    ordering: filters.ordering ?? "-days_in_collection",
    status: filters.status,
    cadence: filters.cadence,
    clinic: filters.clinic,
    patient: filters.patient,
    days_in_collection_min: filters.days_in_collection_min,
    days_in_collection_max: filters.days_in_collection_max,
    invoice_date_after: filters.invoice_date_after,
    invoice_date_before: filters.invoice_date_before,
    min_balance: filters.min_balance,
    max_balance: filters.max_balance,
    search: filters.search ? encodeURIComponent(filters.search) : undefined,
    started_after: filters.started_after,
    started_before: filters.started_before,
    last_contact_after: filters.last_contact_after,
    last_contact_before: filters.last_contact_before,
    max_total_collected: filters.max_total_collected,
    min_total_collected: filters.min_total_collected,
    query: COLLECTIONS_DEFAULT_QUERY
  };
};

export const collectionsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getCollectionsPaginated: createPaginatedEndpoint(builder, {
      queryFn: (filters: CollectionsFilters) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated(
          getDefaultCollectionsFilters(filters)
        );
        return `/collections/?${filtersString}`;
      },
      providesTags: ["Collections"],
      transformResponseFn: (response: CollectionsResponse) => response.results
    }),

    queryCollections: builder.query<CollectionsResponse, CollectionsFilters>({
      query: (filters) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated(
          getDefaultCollectionsFilters(filters)
        );
        return `/collections/?${filtersString}`;
      },
      providesTags: ["Collections"]
    }),

    exportCollectionsCSV: builder.query<
      CSVReportResponse,
      {
        filters: CollectionsFilters;
        title?: string;
        currentClinicId?: number;
        csv_header?: string;
        csv_labels?: string;
      }
    >({
      query: ({
        filters,
        title = "collections",
        currentClinicId,
        csv_header = COLLECTIONS_CSV_HEADER,
        csv_labels = COLLECTIONS_CSV_LABELS
      }) => {
        const csvFilters: any = {
          ordering: filters.ordering ?? "-days_in_collection",
          status: filters.status,
          cadence: filters.cadence,
          clinic: filters.clinic,
          patient: filters.patient,
          days_in_collection_min: filters.days_in_collection_min,
          days_in_collection_max: filters.days_in_collection_max,
          invoice_date_after: filters.invoice_date_after,
          invoice_date_before: filters.invoice_date_before,
          min_balance: filters.min_balance,
          max_balance: filters.max_balance,
          search: filters.search
            ? encodeURIComponent(filters.search)
            : undefined,
          started_after: filters.started_after,
          started_before: filters.started_before,
          last_contact_after: filters.last_contact_after,
          last_contact_before: filters.last_contact_before,
          max_total_collected: filters.max_total_collected,
          min_total_collected: filters.min_total_collected,
          query: COLLECTIONS_CSV_QUERY,
          title,
          csv_header,
          csv_labels,
          current_clinic: currentClinicId
        };

        const filtersString =
          formatValidQueryParamUrlCommaSeparated(csvFilters);
        return {
          url: `/collections/?${filtersString}`,
          headers: {
            Accept: "custom-csv/json"
          }
        };
      }
    }),
    getCollection: builder.query<CollectionRecord, number>({
      query: (id) =>
        `/collections/${id}/?query=${encodeURIComponent(COLLECTION_DETAIL_QUERY)}`,
      providesTags: ["Collections"]
    }),

    updateCollection: builder.mutation<CollectionRecord, UpdateCollectionInput>(
      {
        query: ({ id, ...data }) => ({
          url: `/collections/${id}/`,
          method: "PATCH",
          body: data
        }),
        invalidatesTags: ["Collections", "CollectionOutreach"]
      }
    ),
    // Closing one collection is its own endpoint, not the generic status PATCH: it is the single
    // write a practice that has left Suno Collect may still make, and the access policy names it.
    removeFromCollect: builder.mutation<CollectionRecord, number>({
      query: (id) => ({
        url: `/collections/${id}/remove/`,
        method: "PATCH",
        body: {}
      }),
      invalidatesTags: ["Collections", "CollectionOutreach"]
    }),
    sendOutreach: builder.mutation<CollectionOutreach, TriggerOutreachInput>({
      query: ({ id, ...data }) => ({
        url: `/collections/${id}/send-outreach/`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Collections", "CollectionOutreach"]
    }),
    // One staff task per selected collection, raised in a single batch so the assignee gets one
    // "you have been assigned N tasks" notification instead of one email per task.
    bulkStaffTask: builder.mutation<BulkStaffTaskResult, BulkStaffTaskInput>({
      query: (data) => ({
        url: `/collections/bulk-staff-task/`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Collections", "CollectionOutreach"]
    }),

    getCollectionCounters: builder.query<
      CollectionCounters,
      { clinic?: number; status?: number | number[] }
    >({
      query: (params) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated(params);
        return `/collections/summary/?${filtersString}`;
      },
      providesTags: ["Collections"]
    }),

    getCollectionOutreach: builder.query<
      CollectionOutreach[],
      { collectionId: number }
    >({
      query: ({ collectionId }) =>
        `/collections/outreach/?record=${collectionId}&query=${encodeURIComponent(OUTREACH_QUERY)}&ordering=scheduled_at&limit=1000`,
      providesTags: ["CollectionOutreach"],
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : response.results
    }),

    queryOutreach: builder.query<
      CollectionOutreach[],
      { status?: string; limit?: number }
    >({
      query: (filters) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated({
          ...filters,
          query: OUTREACH_QUERY,
          ordering: "-scheduled_at"
        });
        return `/collections/outreach/?${filtersString}`;
      },
      providesTags: ["CollectionOutreach"],
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : response.results
    }),

    queryOutreachSchedule: builder.query<
      OutreachScheduleResponse,
      OutreachScheduleFilters
    >({
      query: (filters) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated({
          offset: filters.offset ?? 0,
          limit: filters.limit ?? DEFAULT_COLLECTIONS_LIMIT,
          ordering: filters.ordering ?? "scheduled_at",
          status: filters.status,
          scheduled_at__gte: filters.scheduled_at__gte,
          scheduled_at__lte: filters.scheduled_at__lte,
          search: filters.search
            ? encodeURIComponent(filters.search)
            : undefined,
          record: filters.record,
          query: OUTREACH_SCHEDULE_QUERY
        });
        return `/collections/outreach/?${filtersString}`;
      },
      providesTags: ["CollectionOutreach"]
    }),

    updateOutreach: builder.mutation<
      CollectionOutreach,
      { id: number; scheduled_at?: string; status?: number }
    >({
      query: ({ id, ...data }) => ({
        url: `/collections/outreach/${id}/`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["CollectionOutreach", "Collections"]
    }),

    getCollectionDisputes: builder.query<
      CollectionDispute[],
      { collectionId: number }
    >({
      query: ({ collectionId }) =>
        `/collections/${collectionId}/disputes/?query=${encodeURIComponent(DISPUTE_QUERY)}&ordering=-created_at`,
      providesTags: ["CollectionDisputes"],
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : response.results
    }),

    queryDisputes: builder.query<
      CollectionDispute[],
      { status?: string; limit?: number }
    >({
      query: (filters) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated({
          ...filters,
          query: DISPUTE_QUERY,
          ordering: "-created_at"
        });
        return `/collections/disputes/?${filtersString}`;
      },
      providesTags: ["CollectionDisputes"],
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : response.results
    }),
    createDispute: builder.mutation<CollectionDispute, CreateDisputeInput>({
      query: (data) => ({
        url: `/collections/disputes/`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["CollectionDisputes", "Collections"]
    }),
    updateDispute: builder.mutation<CollectionDispute, UpdateDisputeInput>({
      query: ({ id, ...data }) => ({
        url: `/collections/disputes/${id}/`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["CollectionDisputes", "Collections"]
    }),

    getCadenceTemplates: builder.query<
      CollectionCadenceTemplate[],
      { practice?: number }
    >({
      query: (params) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated({
          ...params,
          ordering: "cadence_level,day_offset",
          limit: 1000
        });
        return `/collections/cadence-templates/?${filtersString}`;
      },
      providesTags: ["CollectionCadenceTemplates"],
      transformResponse: (response: CollectionCadenceTemplate[] | any) =>
        Array.isArray(response) ? response : response.results || []
    }),

    getCadenceTemplate: builder.query<CollectionCadenceTemplate, number>({
      query: (id) =>
        `/collections/cadence-templates/${id}/?query=${CADENCE_TEMPLATE_QUERY}`,
      providesTags: ["CollectionCadenceTemplates"]
    }),

    createCadenceTemplate: builder.mutation<
      CollectionCadenceTemplate,
      CreateCadenceTemplateInput
    >({
      query: (data) => ({
        url: "/collections/cadence-templates/",
        method: "POST",
        body: data
      }),
      invalidatesTags: ["CollectionCadenceTemplates"]
    }),

    updateCadenceTemplate: builder.mutation<
      CollectionCadenceTemplate,
      UpdateCadenceTemplateInput
    >({
      query: ({ id, ...data }) => ({
        url: `/collections/cadence-templates/${id}/`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["CollectionCadenceTemplates"]
    }),

    deleteCadenceTemplate: builder.mutation<void, number>({
      query: (id) => ({
        url: `/collections/cadence-templates/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["CollectionCadenceTemplates"]
    }),

    resetCadenceTemplates: builder.mutation<
      {
        deleted_count: number;
        created_count: number;
        templates: CollectionCadenceTemplate[];
      },
      void
    >({
      query: () => ({
        url: "/collections/cadence-templates/reset/",
        method: "POST",
        body: {}
      }),
      invalidatesTags: ["CollectionCadenceTemplates"]
    }),

    bulkUpdateCollections: builder.mutation<
      {
        success_count: number;
        failure_count: number;
        successful_ids: number[];
        failed_records: Array<{
          id: number;
          reason: string;
          patient_name: string | null;
        }>;
        new_status: number;
      },
      { record_ids: number[]; update_status: number }
    >({
      query: (body) => ({
        url: "/collections/bulk-update/",
        method: "PATCH",
        body
      }),
      invalidatesTags: ["Collections"]
    }),

    startCollection: builder.mutation<
      StartCollectionResponse,
      StartCollectionRequest
    >({
      query: (data) => ({
        url: "/collections/start/",
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Collections"]
    }),

    getEnrollmentOffer: builder.query<CollectEnrollmentOffer, number>({
      query: (saleId) => `/collections/enrollment-offer/?sale_id=${saleId}`,
      // Read after the ERA apply has committed, so it must never be served from cache.
      keepUnusedDataFor: 0
    }),

    getSuggestedCollections: builder.query<
      SuggestedSalesResponse,
      {
        limit?: number;
        offset?: number;
        scope?: SuggestedScope;
        includeDismissed?: boolean;
      }
    >({
      query: ({
        limit,
        offset,
        scope = "new",
        includeDismissed = false
      } = {}) =>
        `/collections/suggested/?scope=${scope}${
          limit === undefined ? "" : `&limit=${limit}`
        }${offset ? `&offset=${offset}` : ""}${
          includeDismissed ? "&include_dismissed=true" : ""
        }`,
      providesTags: ["Collections"]
    }),

    bulkDismissSuggestions: builder.mutation<
      { dismissed_count: number },
      { sale_ids: number[] }
    >({
      query: (body) => ({
        url: "/collections/bulk-dismiss/",
        method: "POST",
        body
      }),
      // Refetches the suggestion lists, which is what makes the rows disappear.
      invalidatesTags: ["Collections"]
    }),

    getCollectionPracticeSettings: builder.query<
      CollectionPracticeSettings,
      void
    >({
      query: () => "/collections/settings/",
      providesTags: ["Collections"]
    }),

    createCollectionPracticeSettings: builder.mutation<
      CollectionPracticeSettings,
      Partial<CollectionPracticeSettings>
    >({
      query: (data) => ({
        url: "/collections/settings/",
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Collections"]
    }),

    updateCollectionPracticeSettings: builder.mutation<
      CollectionPracticeSettings,
      Partial<CollectionPracticeSettings>
    >({
      query: (data) => ({
        url: "/collections/settings/",
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["Collections"]
    }),

    getBillingPayments: builder.query<
      BillingPaymentsResponse,
      BillingPaymentsFilters
    >({
      query: (filters) => {
        const filtersString = formatValidQueryParamUrl({
          start_date: filters.start_date,
          end_date: filters.end_date,
          search: filters.search
            ? encodeURIComponent(filters.search)
            : undefined,
          sale: filters.sale,
          has_reversal: filters.has_reversal ? "true" : undefined,
          show_pilot: filters.show_pilot ? "true" : undefined,
          ordering: filters.ordering ?? "-payment_date",
          limit: filters.limit ?? 25,
          offset: filters.offset ?? 0
        });
        return `/collections/billing/payments/?${filtersString}`;
      },
      providesTags: ["CollectionBilling"]
    }),

    exportBillingPaymentsCSV: builder.query<
      CSVReportResponse,
      {
        filters: BillingPaymentsFilters;
        currentClinicId?: number;
        title?: string;
        csv_header?: string;
        csv_labels?: string;
      }
    >({
      query: ({
        filters,
        currentClinicId,
        title = "collections-billing",
        csv_header = COLLECTIONS_BILLING_CSV_HEADER,
        csv_labels = COLLECTIONS_BILLING_CSV_LABELS
      }) => {
        const filtersString = formatValidQueryParamUrl({
          start_date: filters.start_date,
          end_date: filters.end_date,
          search: filters.search
            ? encodeURIComponent(filters.search)
            : undefined,
          sale: filters.sale,
          has_reversal: filters.has_reversal ? "true" : undefined,
          show_pilot: filters.show_pilot ? "true" : undefined,
          ordering: filters.ordering ?? "-payment_date",
          title,
          csv_header,
          csv_labels,
          current_clinic: currentClinicId
        });
        return {
          url: `/collections/billing/payments/?${filtersString}`,
          headers: { Accept: "custom-csv/json" }
        };
      }
    }),

    startNewCadence: builder.mutation<
      CollectionRecord,
      { id: number; cadence?: CadenceLevel }
    >({
      query: ({ id, cadence }) => ({
        url: `/collections/${id}/start-new-cadence/`,
        method: "POST",
        body: cadence !== undefined ? { cadence } : {}
      }),
      invalidatesTags: ["Collections", "CollectionOutreach"]
    }),

    bulkStartNewCadence: builder.mutation<
      BulkStartNewCadenceResponse,
      { record_ids: number[]; cadence?: CadenceLevel }
    >({
      query: ({ record_ids, cadence }) => ({
        url: "/collections/bulk-start-new-cadence/",
        method: "POST",
        // Omitted cadence means "reuse the one that just ran" -- sending null would
        // be rejected as an invalid choice rather than read as absent.
        body: cadence !== undefined ? { record_ids, cadence } : { record_ids }
      }),
      invalidatesTags: ["Collections", "CollectionOutreach"]
    })
  })
});

export const {
  useGetCollectionsPaginatedQuery,
  useQueryCollectionsQuery,
  useLazyQueryCollectionsQuery,
  useGetCollectionQuery,
  useLazyGetCollectionQuery,
  useUpdateCollectionMutation,
  useBulkStaffTaskMutation,
  useRemoveFromCollectMutation,
  useSendOutreachMutation,
  useGetCollectionCountersQuery,
  useLazyGetCollectionCountersQuery,
  useLazyExportCollectionsCSVQuery,
  useBulkUpdateCollectionsMutation,
  useStartCollectionMutation,
  useGetCollectionOutreachQuery,
  useQueryOutreachQuery,
  useQueryOutreachScheduleQuery,
  useLazyQueryOutreachScheduleQuery,
  useUpdateOutreachMutation,
  useGetCollectionDisputesQuery,
  useQueryDisputesQuery,
  useCreateDisputeMutation,
  useUpdateDisputeMutation,
  useGetCadenceTemplatesQuery,
  useGetCadenceTemplateQuery,
  useCreateCadenceTemplateMutation,
  useUpdateCadenceTemplateMutation,
  useDeleteCadenceTemplateMutation,
  useResetCadenceTemplatesMutation,
  useBulkDismissSuggestionsMutation,
  useGetSuggestedCollectionsQuery,
  useLazyGetEnrollmentOfferQuery,
  useGetCollectionPracticeSettingsQuery,
  useUpdateCollectionPracticeSettingsMutation,
  useStartNewCadenceMutation,
  useBulkStartNewCadenceMutation,
  useGetBillingPaymentsQuery,
  useLazyGetBillingPaymentsQuery,
  useLazyExportBillingPaymentsCSVQuery
} = collectionsApi;
